<template>
  <div class="franquias-page">
    <PageHeader
      :title="t('Franquias')"
      :subtitle="t('Gerencie as franquias e suas configurações')"
      icon="pi pi-building"
    >
      <template #actions>
        <Button
          data-testid="franquias-new-button"
          icon="pi pi-plus"
          :label="t('Nova Franquia')"
          severity="secondary"
          @click="handleNewFranquia"
          class="new-button"
        />
      </template>
    </PageHeader>

    <!-- Stats Cards -->
    <div class="stats-cards">
      <div class="stat-card paises">
        <div class="stat-icon">
          <i class="pi pi-globe"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ stats.paises }}</div>
          <div class="stat-label">{{ t('Países') }}</div>
        </div>
        <div class="stat-badge">
          <i class="pi pi-flag"></i>
        </div>
      </div>

      <div class="stat-card estados">
        <div class="stat-icon">
          <i class="pi pi-map"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ stats.estados }}</div>
          <div class="stat-label">{{ t('Estados') }}</div>
        </div>
        <div class="stat-badge">
          <i class="pi pi-compass"></i>
        </div>
      </div>

      <div class="stat-card cidades">
        <div class="stat-icon">
          <i class="pi pi-home"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ stats.cidades }}</div>
          <div class="stat-label">{{ t('Cidades') }}</div>
        </div>
        <div class="stat-badge">
          <i class="pi pi-map-marker"></i>
        </div>
      </div>

      <div class="stat-card total">
        <div class="stat-icon">
          <i class="pi pi-building"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ stats.total || 0 }}</div>
          <div class="stat-label">{{ t('Total de Franquias') }}</div>
        </div>
        <div class="stat-trend">
          <i class="pi pi-chart-line"></i>
        </div>
      </div>
    </div>

    <!-- Table Section -->
    <div class="table-section">
      <franquias-table
        ref="franquiasTableRef"
        :datatables-language-url="datatablesLanguageUrl"
        @edit="handleEditFranquia">
      </franquias-table>
    </div>

    <!-- FranquiasModal is centralized in ModalManager.vue - opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import PageHeader from '../../ui/PageHeader.vue';
import { eventBus } from '../../eventBus';
import { useFranquias } from '../../composables/useFranquias';

type Id = number | string;

interface FranquiasTableInstance {
  refreshData: () => void;
}

interface Stats {
  paises: number;
  cidades: number;
  estados: number;
  total: number;
  idiomas: number;
}

interface Props {
  usuarioVersao?: number;
  translations?: Record<string, unknown>;
  datatablesLanguageUrl?: string;
}

withDefaults(defineProps<Props>(), {
  usuarioVersao: 0,
  translations: () => ({}),
  datatablesLanguageUrl: '',
});

const { t } = useI18n();
const { healthCheck } = useFranquias();
const router = useRouter();

const franquiasTableRef = ref<FranquiasTableInstance | null>(null);
const stats = ref<Stats>({
  paises: 0,
  cidades: 0,
  estados: 0,
  total: 0,
  idiomas: 0,
});

const handleNewFranquia = (): void => {
  eventBus.emit('modal.franquia.open', { action: 'new' });
};

const handleEditFranquia = (franquiaId?: Id): void => {
  eventBus.emit('modal.franquiasWizard.open', { id: franquiaId });
};

const handleConfigsAvancadas = (payload?: { franquiaId?: Id }): void => {
  if (!payload) return;
  router.push({
    name: 'FranquiasConfiguracoesAvancadas',
    params: { id: String(payload.franquiaId ?? '') },
  });
};

const handleRefresh = (): void => {
  if (franquiasTableRef.value) {
    franquiasTableRef.value.refreshData();
  }
};

const updateStats = (data?: Partial<Stats>): void => {
  if (data) {
    stats.value = {
      paises: data.paises || 0,
      cidades: data.cidades || 0,
      estados: data.estados || 0,
      total: data.total || 0,
      idiomas: data.idiomas || 0,
    };
  }
};

const checkServiceHealth = async (): Promise<void> => {
  try {
    await healthCheck();
  } catch {
    eventBus.emit('globalMessage.warning', t('Aviso: Serviço de franquias pode estar indisponível'));
  }
};

onMounted(() => {
  checkServiceHealth();

  eventBus.on('franquias.refresh', handleRefresh);
  eventBus.on('franquias.stats-updated', updateStats);
  eventBus.on('franquias.new', handleNewFranquia);
  eventBus.on('franquias.edit', handleEditFranquia);
  eventBus.on('franquias.configs.avancadas', handleConfigsAvancadas);
});

onBeforeUnmount(() => {
  // BUGFIX: previously eventBus.off('franquias.refresh') passed no handler ref and was a no-op.
  eventBus.off('franquias.refresh', handleRefresh);
  eventBus.off('franquias.stats-updated', updateStats);
  eventBus.off('franquias.new', handleNewFranquia);
  eventBus.off('franquias.edit', handleEditFranquia);
  eventBus.off('franquias.configs.avancadas', handleConfigsAvancadas);
});

defineExpose({
  handleNewFranquia,
  handleEditFranquia,
  updateStats,
});
</script>

<style scoped>
.franquias-page {
  padding: 24px;
  min-height: 100vh;
  background: var(--surface-ground);
}

.new-button {
  font-weight: 500;
}

.new-button :deep(.p-button-label) {
  color: white;
}

.new-button :deep(.p-button-icon) {
  color: white;
}

/* Stats Cards */
.stats-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 24px;
}

.stat-card {
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: var(--surface-border);
  transition: background 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

/* Cores do indicador por tipo */
.stat-card.paises::before { background: linear-gradient(90deg, var(--tenant-secondary-color), var(--tenant-secondary-hover-color, var(--tenant-secondary-color))); }
.stat-card.estados::before { background: linear-gradient(90deg, #22c55e, #16a34a); }
.stat-card.cidades::before { background: linear-gradient(90deg, var(--tenant-secondary-color), var(--tenant-secondary-hover-color, var(--tenant-secondary-color))); }
.stat-card.total::before { background: linear-gradient(90deg, var(--tenant-secondary-color), var(--tenant-secondary-hover-color, var(--tenant-secondary-color))); }

.stat-icon {
  width: 56px;
  height: 56px;
  background: var(--surface-100);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-icon i {
  font-size: 26px;
  color: var(--text-color-secondary);
}

.stat-card.paises .stat-icon {
  background: rgba(var(--app-secondary-color-rgb), 0.12);
}
.stat-card.paises .stat-icon i { color: var(--tenant-secondary-color); }

.stat-card.estados .stat-icon {
  background: #dcfce7;
}
.stat-card.estados .stat-icon i { color: #16a34a; }

.stat-card.cidades .stat-icon {
  background: rgba(var(--app-secondary-color-rgb), 0.12);
}
.stat-card.cidades .stat-icon i { color: var(--tenant-secondary-color); }

.stat-card.total .stat-icon {
  background: rgba(var(--app-secondary-color-rgb), 0.12);
}
.stat-card.total .stat-icon i { color: var(--tenant-secondary-color); }

.stat-content {
  flex: 1;
  min-width: 0;
}

.stat-value {
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 4px;
  color: var(--text-color);
}

.stat-label {
  font-size: 13px;
  color: var(--text-color-secondary);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-badge {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 14px;
}

.stat-card.paises .stat-badge {
  background: rgba(var(--app-secondary-color-rgb), 0.12);
}
.stat-card.paises .stat-badge i { color: var(--tenant-secondary-color); }

.stat-card.estados .stat-badge {
  background: #dcfce7;
}
.stat-card.estados .stat-badge i { color: #16a34a; }

.stat-card.cidades .stat-badge {
  background: rgba(var(--app-secondary-color-rgb), 0.12);
}
.stat-card.cidades .stat-badge i { color: var(--tenant-secondary-color); }

.stat-trend {
  font-size: 20px;
  color: var(--text-color-secondary);
  opacity: 0.5;
}

/* Table Section */
.table-section {
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  padding: 16px;
}

/* Responsive */
@media (max-width: 1200px) {
  .stats-cards {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .franquias-page {
    padding: 16px;
  }

  .new-button {
    width: 100%;
  }

  .stats-cards {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .stat-card {
    padding: 16px;
  }

  .stat-value {
    font-size: 26px;
  }
}
</style>
